import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { css, keyframes } from '@emotion/css';

import { LATON } from '#components/mobile/ayuda/OjoBuey';
import { Icono } from '#components/mobile/ui/Icono';
import { color, espacio, movimiento } from '#components/mobile/ui/tokens';

import {
  aspectoWidget,
  filasInicio,
  TAMANOS_PERMITIDOS,
} from './modeloWidgets';
import type { IdWidget, Tamano, WidgetInicio } from './modeloWidgets';
import { nombreWidget } from './widgets/registro';

/** Mantener pulsada una tarjeta (fuera de editar) abre el modo editar. */
const MANTENER_MS = 450;
/** En editar basta un instante quieto para levantarla. */
const MANTENER_EDITANDO_MS = 180;
/** Si el dedo se mueve más que esto antes, es que quiere desplazar la página. */
const TOLERANCIA_PX = 8;
/** Margen junto al borde de la pantalla que hace desplazar la página sola. */
const BORDE_AUTO_PX = 80;
/** Pausa entre dos cambios de sitio, para que no tiemble entre dos huecos. */
const PAUSA_CAMBIO_MS = 140;

const mecer = keyframes({
  from: { transform: 'rotate(-0.5deg)' },
  to: { transform: 'rotate(0.5deg) translateY(-1px)' },
});

const aparece = keyframes({
  from: { opacity: 0, transform: 'scale(0.6)' },
  to: { opacity: 1, transform: 'none' },
});

const claseRejilla = css({
  position: 'relative',
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: espacio.tarjetas,
  alignItems: 'start',
  // Bandas de las dos columnas al editar (sin rayas horizontales).
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: `-6px -6px 0`,
    borderRadius: 16,
    pointerEvents: 'none',
    opacity: 0,
    transition: 'opacity 300ms',
    background: `linear-gradient(90deg, ${LATON.oscuro}17 0 calc(50% - ${espacio.tarjetas / 2}px), transparent calc(50% - ${espacio.tarjetas / 2}px) calc(50% + ${espacio.tarjetas / 2}px), ${LATON.oscuro}17 calc(50% + ${espacio.tarjetas / 2}px))`,
  },
  '&[data-editando]::before': { opacity: 1 },
});

const claseItem = css({
  position: 'relative',
  minWidth: 0,
  // Sin menú del sistema ni selección de texto al mantener pulsado.
  WebkitTouchCallout: 'none',
  userSelect: 'none',
  WebkitUserSelect: 'none',
  outline: 'none',
  // Una tarjeta que hoy no pinta nada no deja hueco (fuera de editar).
  '&:has(> [data-mece] > [data-contenido]:empty):not([data-editando])': {
    display: 'none',
  },
  '&[data-arrastrando]': { zIndex: 10 },
  '& > [data-mece]': { borderRadius: 20 },
  // Al editar, cada tarjeta es una pieza opaca sobre las bandas (y al
  // arrastrarla no se transparenta lo de debajo).
  '&[data-editando] > [data-mece]': {
    backgroundColor: color.bg,
    animation: `${mecer} 1.2s ease-in-out infinite alternate`,
    boxShadow: `0 0 0 1.5px ${LATON.oscuro}`,
    cursor: 'grab',
  },
  '&[data-editando]:nth-of-type(2n) > [data-mece]': {
    animationDelay: '-0.55s',
    animationDuration: '1.25s',
  },
  '&[data-editando]:nth-of-type(3n) > [data-mece]': {
    animationDelay: '-0.3s',
  },
  '&[data-elegida] > [data-mece]': {
    boxShadow: `0 0 0 3px ${LATON.oscuro}`,
  },
  '&:focus-visible > [data-mece]': {
    boxShadow: `0 0 0 3px ${color.accent}`,
  },
  '&[data-arrastrando] > [data-mece]': {
    animation: 'none',
    transform: 'scale(1.03)',
    boxShadow: `0 18px 36px rgba(14, 20, 36, 0.28), 0 0 0 1.5px ${LATON.oscuro}`,
    transition: 'transform 150ms, box-shadow 150ms',
  },
  // Al editar, la tarjeta no se abre: tocarla la elige.
  '&[data-editando] [data-contenido]': { pointerEvents: 'none' },
  '& [data-vacio]': { display: 'none' },
  '& [data-contenido]:empty + [data-vacio]': { display: 'flex' },
  '@media (prefers-reduced-motion: reduce)': {
    '&[data-editando] > [data-mece]': { animation: 'none' },
  },
});

const claseControl = css({
  position: 'absolute',
  zIndex: 2,
  width: 32,
  height: 32,
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 2px 6px rgba(14, 20, 36, 0.3)',
  animation: `${aparece} 300ms ${movimiento.muelle} both`,
  // Zona táctil de 44 px sin agrandar el dibujo.
  '&::after': { content: '""', position: 'absolute', inset: -6 },
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

const claseHueco = css({
  minHeight: 64,
  alignSelf: 'stretch',
  border: `2px dashed ${LATON.oscuro}`,
  borderRadius: 16,
  background: `${LATON.claro}1f`,
  color: LATON.oscuro,
  fontSize: 26,
  fontWeight: 800,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

type Props = {
  /** Las tarjetas que se ven, en orden. */
  widgets: readonly WidgetInicio[];
  editando: boolean;
  elegida: IdWidget | null;
  onEntrarEdicion: () => void;
  onElegir: (id: IdWidget | null) => void;
  onReordenar: (orden: IdWidget[]) => void;
  onQuitar: (id: IdWidget) => void;
  onTamano: (id: IdWidget) => void;
  /** «+» junto a una pequeña sola: añadir una tarjeta detrás de ella. */
  onAnadir: (despues: IdWidget) => void;
  pintar: (
    widget: WidgetInicio,
    aspecto: { tamano: Tamano; media: boolean },
  ) => ReactNode;
};

type Arrastre = {
  item: HTMLElement;
  id: IdWidget;
  objetivo: EventTarget | null;
  pointerId: number;
  pointerType: string;
  x0: number;
  y0: number;
  x: number;
  y: number;
  temporizador: ReturnType<typeof setTimeout> | null;
  levantado: boolean;
  /** Dónde se cogió la tarjeta, respecto a su esquina. */
  gx: number;
  gy: number;
  orden: IdWidget[];
  inicial: IdWidget[];
  ultimoCambio: number;
  scroll: HTMLElement;
  frame: number | null;
};

function contenedorDesplazable(el: HTMLElement): HTMLElement {
  let p = el.parentElement;
  while (p) {
    const { overflowY } = getComputedStyle(p);
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      p.scrollHeight > p.clientHeight
    ) {
      return p;
    }
    p = p.parentElement;
  }
  return (document.scrollingElement as HTMLElement) ?? document.body;
}

function sinMovimiento() {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

type Caja = { x: number; y: number; w: number; h: number };

function cajaDe(el: HTMLElement): Caja {
  return {
    x: el.offsetLeft,
    y: el.offsetTop,
    w: el.offsetWidth,
    h: el.offsetHeight,
  };
}

/**
 * Orden nuevo si el centro de la tarjeta arrastrada (en coordenadas de la
 * rejilla) está sobre otra tarjeta o sobre el hueco de una pequeña sola.
 */
export function ordenAlArrastrar(
  orden: readonly IdWidget[],
  id: IdWidget,
  centro: { x: number; y: number },
  objetivos: readonly { id: IdWidget; hueco: boolean; caja: Caja }[],
  anchoRejilla: number,
): IdWidget[] {
  const resto = orden.filter(x => x !== id);
  const dentro = objetivos.find(
    o =>
      o.id !== id &&
      centro.x >= o.caja.x &&
      centro.x <= o.caja.x + o.caja.w &&
      centro.y >= o.caja.y &&
      centro.y <= o.caja.y + o.caja.h,
  );
  let en: number;
  if (dentro) {
    const i = resto.indexOf(dentro.id);
    if (i < 0) {
      return [...orden];
    }
    if (dentro.hueco) {
      en = i + 1;
    } else {
      const entera = dentro.caja.w > anchoRejilla * 0.75;
      const antes = entera
        ? centro.y < dentro.caja.y + dentro.caja.h / 2
        : centro.x < dentro.caja.x + dentro.caja.w / 2;
      en = antes ? i : i + 1;
    }
  } else {
    const cajas = objetivos.filter(o => o.id !== id).map(o => o.caja);
    if (cajas.length === 0) {
      return [...orden];
    }
    const arriba = Math.min(...cajas.map(c => c.y));
    const abajo = Math.max(...cajas.map(c => c.y + c.h));
    if (centro.y > abajo) {
      en = resto.length;
    } else if (centro.y < arriba) {
      en = 0;
    } else {
      return [...orden];
    }
  }
  return [...resto.slice(0, en), id, ...resto.slice(en)];
}

/**
 * Rejilla de Inicio en dos columnas, como los widgets del móvil. Mantener
 * pulsada una tarjeta abre el modo editar ahí mismo y, sin soltar, se
 * arrastra; al editar, las tarjetas se mecen, llevan la ✕ (quitar) y el asa
 * de latón (tamaño), y tocar una la elige para la barra Subir / Bajar /
 * Tamaño / Quitar.
 */
export function RejillaInicio({
  widgets,
  editando,
  elegida,
  onEntrarEdicion,
  onElegir,
  onReordenar,
  onQuitar,
  onTamano,
  onAnadir,
  pintar,
}: Props) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  // Orden mientras se arrastra (y hasta que llega el guardado).
  const [ordenVivo, setOrdenVivo] = useState<IdWidget[] | null>(null);
  const soltadoRef = useRef(false);
  const arrastreRef = useRef<Arrastre | null>(null);
  const colocarRef = useRef<(() => void) | null>(null);
  const posiciones = useRef(new Map<string, { x: number; y: number }>());

  const callbacks = useRef({
    onEntrarEdicion,
    onElegir,
    onReordenar,
    editando,
  });
  useEffect(() => {
    callbacks.current = { onEntrarEdicion, onElegir, onReordenar, editando };
  });

  // Llega la lista guardada: se deja de usar el orden del arrastre.
  useEffect(() => {
    if (soltadoRef.current) {
      soltadoRef.current = false;
      setOrdenVivo(null);
    }
  }, [widgets]);

  const porId = new Map(widgets.map(w => [w.id, w]));
  const lista =
    ordenVivo && ordenVivo.length === widgets.length
      ? ordenVivo
          .map(id => porId.get(id))
          .filter((w): w is WidgetInicio => w != null)
      : widgets;
  const filas = filasInicio(lista);

  // Las demás tarjetas se apartan con suavidad (FLIP) al cambiar de sitio.
  useLayoutEffect(() => {
    const cont = ref.current;
    if (!cont) {
      return;
    }
    const nuevas = new Map<string, { x: number; y: number }>();
    const animar = (editando || ordenVivo != null) && !sinMovimiento();
    for (const el of Array.from(cont.children)) {
      if (!(el instanceof HTMLElement)) {
        continue;
      }
      const clave =
        el.dataset.widgetId ?? (el.dataset.huecoDe && `+${el.dataset.huecoDe}`);
      if (!clave) {
        continue;
      }
      const p = { x: el.offsetLeft, y: el.offsetTop };
      nuevas.set(clave, p);
      const antes = posiciones.current.get(clave);
      if (
        animar &&
        antes &&
        (antes.x !== p.x || antes.y !== p.y) &&
        el !== arrastreRef.current?.item &&
        typeof el.animate === 'function'
      ) {
        el.animate(
          [
            { transform: `translate(${antes.x - p.x}px, ${antes.y - p.y}px)` },
            { transform: 'none' },
          ],
          { duration: 260, easing: movimiento.suave },
        );
      }
    }
    posiciones.current = nuevas;
    colocarRef.current?.();
  });

  useEffect(() => {
    const cont = ref.current;
    if (!cont) {
      return;
    }
    let cancelandoPulsacion = false;

    const quitarVentana = () => {
      window.removeEventListener('pointermove', onMove, true);
      window.removeEventListener('pointerup', onUp, true);
      window.removeEventListener('pointercancel', onCancel, true);
    };

    const terminar = () => {
      const a = arrastreRef.current;
      if (!a) {
        return;
      }
      if (a.temporizador) {
        clearTimeout(a.temporizador);
      }
      if (a.frame != null) {
        cancelAnimationFrame(a.frame);
      }
      if (a.levantado) {
        a.item.style.transform = '';
        delete a.item.dataset.arrastrando;
      }
      arrastreRef.current = null;
      quitarVentana();
    };

    const objetivos = () =>
      Array.from(cont.children).flatMap(el => {
        if (!(el instanceof HTMLElement)) {
          return [];
        }
        const id = el.dataset.widgetId ?? el.dataset.huecoDe;
        if (!id || el.offsetHeight === 0) {
          return [];
        }
        return [
          {
            id: id as IdWidget,
            hueco: el.dataset.huecoDe != null,
            caja: cajaDe(el),
          },
        ];
      });

    const colocar = () => {
      const a = arrastreRef.current;
      if (!a?.levantado) {
        return;
      }
      const g = cont.getBoundingClientRect();
      const x = a.x - g.left - a.gx;
      const y = a.y - g.top - a.gy;
      a.item.style.transform = `translate(${x - a.item.offsetLeft}px, ${y - a.item.offsetTop}px)`;
      const ahora = performance.now();
      if (ahora - a.ultimoCambio < PAUSA_CAMBIO_MS) {
        return;
      }
      const nuevo = ordenAlArrastrar(
        a.orden,
        a.id,
        { x: x + a.item.offsetWidth / 2, y: y + a.item.offsetHeight / 2 },
        objetivos(),
        cont.clientWidth,
      );
      if (nuevo.join() !== a.orden.join()) {
        a.orden = nuevo;
        a.ultimoCambio = ahora;
        navigator.vibrate?.(8);
        setOrdenVivo(nuevo);
      }
    };
    colocarRef.current = colocar;

    // Desplaza la página sola cuando la tarjeta llega arriba o abajo.
    const autoDesplazar = () => {
      const a = arrastreRef.current;
      if (!a?.levantado) {
        return;
      }
      const arriba = Math.max(0, a.scroll.getBoundingClientRect().top);
      const abajo = window.innerHeight;
      let v = 0;
      if (a.y < arriba + BORDE_AUTO_PX) {
        v = -Math.ceil((arriba + BORDE_AUTO_PX - a.y) / 6);
      } else if (a.y > abajo - BORDE_AUTO_PX) {
        v = Math.ceil((a.y - (abajo - BORDE_AUTO_PX)) / 6);
      }
      if (v) {
        a.scroll.scrollTop += v;
      }
      colocar();
      a.frame = requestAnimationFrame(autoDesplazar);
    };

    const levantar = () => {
      const a = arrastreRef.current;
      if (!a) {
        return;
      }
      a.temporizador = null;
      a.levantado = true;
      const { editando } = callbacks.current;
      if (!editando) {
        // La tarjeta no debe abrirse al soltar: se cancela su pulsación.
        cancelandoPulsacion = true;
        a.objetivo?.dispatchEvent(
          new PointerEvent('pointercancel', {
            bubbles: true,
            pointerId: a.pointerId,
            pointerType: a.pointerType,
          }),
        );
        cancelandoPulsacion = false;
        callbacks.current.onEntrarEdicion();
      }
      navigator.vibrate?.(15);
      const r = a.item.getBoundingClientRect();
      a.gx = a.x0 - r.left;
      a.gy = a.y0 - r.top;
      a.inicial = filasActuales();
      a.orden = [...a.inicial];
      a.scroll = contenedorDesplazable(cont);
      a.item.dataset.arrastrando = 'true';
      callbacks.current.onElegir(null);
      a.frame = requestAnimationFrame(autoDesplazar);
    };

    const filasActuales = () =>
      Array.from(cont.children)
        .map(el => (el as HTMLElement).dataset.widgetId)
        .filter((id): id is IdWidget => id != null);

    const soltar = () => {
      const a = arrastreRef.current;
      if (!a) {
        return;
      }
      const final = a.orden;
      const cambiado = final.join() !== a.inicial.join();
      terminar();
      if (cambiado) {
        soltadoRef.current = true;
        callbacks.current.onReordenar(final);
      } else {
        setOrdenVivo(null);
      }
      // El «click» que llega tras soltar no abre ni elige nada.
      const bloquear = (e: MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
      };
      window.addEventListener('click', bloquear, true);
      setTimeout(
        () => window.removeEventListener('click', bloquear, true),
        350,
      );
    };

    function onMove(e: PointerEvent) {
      const a = arrastreRef.current;
      if (!a || e.pointerId !== a.pointerId) {
        return;
      }
      if (!a.levantado) {
        if (Math.hypot(e.clientX - a.x0, e.clientY - a.y0) > TOLERANCIA_PX) {
          terminar();
        }
        return;
      }
      e.preventDefault();
      a.x = e.clientX;
      a.y = e.clientY;
      colocar();
    }

    function onUp(e: PointerEvent) {
      const a = arrastreRef.current;
      if (!a || e.pointerId !== a.pointerId) {
        return;
      }
      if (!a.levantado) {
        const { editando } = callbacks.current;
        const id = a.id;
        terminar();
        // Al editar, un toque elige la tarjeta (barra de acciones).
        if (editando) {
          callbacks.current.onElegir(id);
        }
        return;
      }
      // Nadie más ve este «soltar» (ni el botón de la tarjeta).
      e.stopPropagation();
      e.preventDefault();
      soltar();
    }

    function onCancel(e: PointerEvent) {
      const a = arrastreRef.current;
      if (cancelandoPulsacion || !a || e.pointerId !== a.pointerId) {
        return;
      }
      if (a.levantado) {
        soltar();
      } else {
        terminar();
      }
    }

    const onDown = (e: PointerEvent) => {
      if (
        arrastreRef.current ||
        (e.pointerType === 'mouse' && e.button !== 0)
      ) {
        return;
      }
      const destino = e.target as Element | null;
      if (destino?.closest('[data-control]')) {
        return;
      }
      const item = destino?.closest<HTMLElement>('[data-widget-id]');
      if (!item || item.parentElement !== cont) {
        return;
      }
      const editando = callbacks.current.editando;
      arrastreRef.current = {
        item,
        id: item.dataset.widgetId as IdWidget,
        objetivo: e.target,
        pointerId: e.pointerId,
        pointerType: e.pointerType,
        x0: e.clientX,
        y0: e.clientY,
        x: e.clientX,
        y: e.clientY,
        temporizador: setTimeout(
          levantar,
          editando ? MANTENER_EDITANDO_MS : MANTENER_MS,
        ),
        levantado: false,
        gx: 0,
        gy: 0,
        orden: [],
        inicial: [],
        ultimoCambio: 0,
        scroll: cont,
        frame: null,
      };
      window.addEventListener('pointermove', onMove, true);
      window.addEventListener('pointerup', onUp, true);
      window.addEventListener('pointercancel', onCancel, true);
    };

    // Con la tarjeta levantada, el dedo arrastra y no desplaza la página.
    const onTouchMove = (e: TouchEvent) => {
      if (arrastreRef.current?.levantado && e.cancelable) {
        e.preventDefault();
      }
    };
    const onContextMenu = (e: Event) => {
      if (arrastreRef.current) {
        e.preventDefault();
      }
    };

    cont.addEventListener('pointerdown', onDown);
    cont.addEventListener('touchmove', onTouchMove, { passive: false });
    cont.addEventListener('contextmenu', onContextMenu);
    return () => {
      terminar();
      colocarRef.current = null;
      cont.removeEventListener('pointerdown', onDown);
      cont.removeEventListener('touchmove', onTouchMove);
      cont.removeEventListener('contextmenu', onContextMenu);
    };
  }, []);

  return (
    <div
      ref={ref}
      className={claseRejilla}
      data-editando={editando || undefined}
      data-testid="inicio-widgets"
    >
      {filas.flatMap(fila => {
        const nodos = fila.widgets.map(w => {
          const aspecto = aspectoWidget(w);
          // Una pequeña sola se ve a lo ancho, salvo al editar.
          const media = aspecto.media && (editando || !fila.sola);
          const nombre = nombreWidget(t, w.id);
          return (
            <div
              key={w.id}
              data-widget-id={w.id}
              data-editando={editando || undefined}
              data-elegida={(editando && elegida === w.id) || undefined}
              className={claseItem}
              style={{ gridColumn: media ? 'span 1' : '1 / -1' }}
              {...(editando && {
                role: 'button',
                tabIndex: 0,
                'aria-label': nombre,
                'aria-pressed': elegida === w.id,
                onKeyDown: (e: KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onElegir(elegida === w.id ? null : w.id);
                  }
                },
              })}
            >
              <div data-mece="">
                <div data-contenido="" inert={editando || undefined}>
                  {pintar(w, { tamano: aspecto.tamano, media })}
                </div>
                {editando && (
                  <div
                    data-vacio=""
                    style={{
                      minHeight: 64,
                      padding: '10px 14px',
                      borderRadius: 20,
                      backgroundColor: color.surface,
                      flexDirection: 'column',
                      justifyContent: 'center',
                      gap: 2,
                    }}
                  >
                    <span style={{ fontWeight: 800, color: color.fg }}>
                      {nombre}
                    </span>
                    <span style={{ fontSize: 13, color: color.fg3 }}>
                      <Trans>Nothing to show today</Trans>
                    </span>
                  </div>
                )}
              </div>
              {editando && (
                <Button
                  variant="bare"
                  data-control=""
                  data-testid="inicio-quitar"
                  aria-label={t('Remove {{name}}', { name: nombre })}
                  onPress={() => onQuitar(w.id)}
                  className={claseControl}
                  style={{
                    left: -9,
                    top: -9,
                    borderRadius: '50%',
                    border: `2.5px solid ${LATON.oscuro}`,
                    backgroundColor: '#fff',
                    color: '#1b2433',
                  }}
                >
                  <Icono nombre="x" size={14} />
                </Button>
              )}
              {editando && TAMANOS_PERMITIDOS[w.id].length > 1 && (
                <Button
                  variant="bare"
                  data-control=""
                  data-testid="inicio-asa"
                  aria-label={t('Size of {{name}}', { name: nombre })}
                  onPress={() => onTamano(w.id)}
                  className={claseControl}
                  style={{
                    right: -7,
                    bottom: -7,
                    borderRadius: 10,
                    background: `linear-gradient(135deg, ${LATON.claro}, ${LATON.medio} 60%, ${LATON.oscuro})`,
                  }}
                >
                  <svg
                    viewBox="0 0 18 18"
                    width={18}
                    height={18}
                    aria-hidden="true"
                  >
                    <path
                      d="M4 8V4h4M14 10v4h-4M4 4l10 10"
                      fill="none"
                      stroke="#3d2a06"
                      strokeWidth={2.2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Button>
              )}
            </div>
          );
        });
        const sola = fila.widgets[0];
        if (editando && fila.sola && sola) {
          nodos.push(
            <Button
              key={`+${sola.id}`}
              variant="bare"
              data-hueco-de={sola.id}
              data-control=""
              aria-label={t('Add a card here')}
              onPress={() => onAnadir(sola.id)}
              className={claseHueco}
            >
              +
            </Button>,
          );
        }
        return nodos;
      })}
    </div>
  );
}
